// src/pages/AboutPage.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../styles/about.css';

const pick = (arr, idx, fallback) =>
  Array.isArray(arr) && arr[idx] ? arr[idx] : fallback;

const AboutPage = ({ data: outerData }) => {
  const [innerData, setInnerData] = useState(null);
  const data = outerData || innerData;

  // === 숫자 카운트 관련 ===
  const statsSectionRef = useRef(null);
  const countedOnceRef = useRef(false);
  const rafIdsRef = useRef([]);

  useEffect(() => {
    if (outerData) return;
    (async () => {
      const res = await fetch('/content.json', { cache: 'no-cache' });
      const json = await res.json();
      setInnerData(json);
    })().catch(console.error);
  }, [outerData]);

  const heroBg =
    data?.hero?.bg ||
    'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=2070&q=80';

  const gallery = data?.aboutImages || [];
  const services = data?.tourImages?.cards || [];

  const crossImg1 = pick(
    gallery,
    0,
    'https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=2070&q=80'
  );
  const crossImg2 = pick(
    gallery,
    1,
    'https://images.unsplash.com/photo-1583212292454-1fe6229603b7?auto=format&fit=crop&w=2070&q=80'
  );

  const act1 = pick(services, 0, { bg: crossImg1, title: '수영장 트레이닝' });
  const act2 = pick(services, 1, { bg: crossImg2, title: '오픈워터' });
  const act3 = pick(services, 2, { bg: heroBg, title: '펀다이빙' });

  const activities = [
    { title: act1.title, img: act1.bg },
    { title: act2.title, img: act2.bg },
    { title: act3.title, img: act3.bg },
  ];

  const statItems =
    data?.stats?.items?.length
      ? data.stats.items
      : [
          { value: 2398, unit: '명', label: '누적 수료 교육생' },
          { value: 1472, unit: '명', label: '누적 자격증 발급' },
          { value: 389,  unit: '명', label: '자격증 발급 프로그램' },
          { value: 12,   unit: '년', label: '숙련 및 협력 강사진' },
        ];

  // === 리뷰 리스트 (HomePage 형식에 맞춰 avatar 필드 사용) ===
  const reviews = useMemo(() => {
    if (Array.isArray(data?.reviews) && data.reviews.length) {
      return data.reviews.map((r) => ({
        name: r.name,
        text: r.text,
        avatar: r.avatar || r.img,
      }));
    }
    return [
      {
        name: '김OO님',
        avatar:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
        text: '처음 다이빙을 배울 때 걱정이 많았는데, 강사님들의 세심한 지도로 안전하게 배울 수 있었습니다.',
      },
      {
        name: '박OO님',
        avatar:
          'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
        text: '체계적인 교육과정과 최신 장비로 안전하고 즐거운 다이빙을 배울 수 있었어요.',
      },
      {
        name: '이OO님',
        avatar:
          'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
        text: '전문적인 지식과 경험을 바탕으로 한 수업이 정말 도움이 많이 되었습니다.',
      },
      {
        name: '최OO님',
        avatar:
          'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
        text: '실용적인 응급처치 교육으로 안전 의식을 높일 수 있었습니다. 정말 유익한 시간이었어요.',
      },
    ];
  }, [data]);

  // ✅ YouTube Hero Video
  const YT_ID = 'edKLZ1KQK7U';
  const youtubeEmbedUrl = `https://www.youtube.com/embed/${YT_ID}?autoplay=0&mute=1&controls=1&rel=0&modestbranding=1`;

  // ===== 숫자 카운트업 애니메이션 =====
  useEffect(() => {
    const sectionEl = statsSectionRef.current;
    if (!sectionEl) return;

    const animateCount = (el, to, duration = 1200) => {
      const prefersReduced =
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const target = Number(to);
      if (!isFinite(target)) {
        el.textContent = String(to);
        return;
      }

      if (prefersReduced || duration <= 0) {
        el.textContent = target.toLocaleString('ko-KR');
        return;
      }

      let start = null;
      const startVal = 0;

      const step = (ts) => {
        if (start === null) start = ts;
        const progress = Math.min(1, (ts - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = Math.round(startVal + (target - startVal) * eased);
        el.textContent = current.toLocaleString('ko-KR');
        if (progress < 1) {
          const id = requestAnimationFrame(step);
          rafIdsRef.current.push(id);
        }
      };

      const id = requestAnimationFrame(step);
      rafIdsRef.current.push(id);
    };

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !countedOnceRef.current) {
            countedOnceRef.current = true;
            const els = sectionEl.querySelectorAll('.stat-number b[data-value]');
            els.forEach((b) => {
              const to = b.getAttribute('data-value');
              b.textContent = '0';
              const base = 1000;
              const extra = Math.min(800, String(to).length * 150);
              animateCount(b, to, base + extra);
            });
            io.disconnect();
          }
        });
      },
      { root: null, threshold: 0.25 }
    );

    io.observe(sectionEl);

    return () => {
      io.disconnect();
      rafIdsRef.current.forEach((id) => cancelAnimationFrame(id));
      rafIdsRef.current = [];
    };
  }, [statItems]);

  if (!data) return null;

  return (
    <>
      {/* ⬇️ About 전용 본문만 렌더 (히어로/탭은 AboutLayout이 처리) */}
      <section className="company-intro">
        <div className="container">
          <div className="intro-header">
            <h2>회사 소개</h2>
            <p>안녕하세요, 다이버 전문 교육 브랜드 다이빙이지 입니다.</p>
          </div>

          <div className="intro-content">
            <div className="intro-text">
              <p className="intro-description">
                <strong>다이빙이지와 함께하는 수중 경험은 단순한 레저를 넘어,</strong>
                <br />
                신비롭고 아름다운 세계를 직접 마주하는 특별한 여정입니다.
              </p>

              {/* ✅ 유튜브 히어로 영상 */}
              <div className="intro-hero-media intro-hero-video">
                <div className="video-embed">
                  <iframe
                    src={youtubeEmbedUrl}
                    title="Diving Easy YouTube"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
                <div className="badge">DIVING EASY</div>
              </div>

              {/* ✅ 원래 이미지(원하면 삭제 가능) */}
              {/* 
              <div className="intro-hero-media">
                <img src={heroBg} alt="Free Diving Banner" />
                <div className="badge">DIVING EASY</div>
              </div>
              */}
            </div>
          </div>
        </div>
      </section>

      <section className="content-sections">
        <div className="container">
          <div className="content-grid">
            <div className="content-item">
              <div className="content-text">
                <h3>
                  누구나 쉽고,<br className="desktop-br" />편안하게 —<br />바다와 연결될 수 있도록.
                </h3>
                <div className="content-line" />
                <p>
                  다이빙이지는 이름 그대로, 누구나 쉽고 편안하게 바다와 연결될 수 있도록 돕는 다이빙 전문 교육 업체입니다. 첫 다이빙을 앞둔 초보자부터 자격증 취득을 목표로 한 진지한 입문자까지, 각자의 속도와 눈높이에 맞춰 안전하고 체계적인 교육을 제공합니다. “다이빙은 어렵다”는 편견을 깨고, 바다라는 놀라운 세계에 대한 문턱을 낮추는 것이 우리의 시작이자 목표입니다.
                </p>
              </div>
              <div className="content-image">
                <img src={crossImg1} alt="자연 암초와 다이버" />
              </div>
            </div>

            <div className="content-item reverse">
              <div className="content-image">
                <img src={crossImg2} alt="수영장에서의 교육" />
              </div>
              <div className="content-text">
                <h3>
                  바다 속 감동과<br className="desktop-br" />경이로움을<br />고객과 함께,
                </h3>
                <div className="content-line" />
                <p>
                  프리다이빙은 단순히 숨을 참는 스포츠가 아니라, 
                  자신의 한계에 천천히 다가가 조금씩 넘어서는 과정 자체가 매력인 활동입니다. 
                  처음엔 5m도 긴장되던 수심이 호흡 훈련과 단계별 강습을 통해 점차 편안해지고, 
                  마침내 스스로 새로운 깊이에 도전하게 되는데, 
                  이러한 작은 성취들이 쌓이면서 프리다이빙은 단순한 운동을 넘어 
                  자신감과 용기를 주는 삶의 일부가 됩니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ⬇️ 통계 섹션: ref 연결 */}
      <section className="about-stats" ref={statsSectionRef}>
        <div className="container">
          <div className="stats-header">
            <h2>프리다이빙&스쿠버다이빙 전문 교육기관</h2>
            <p>
              다이빙이지는 무엇보다 ‘사람’ 중심의 경험을 중시합니다.
              우리는 바다가 주는 감동과 경이로움을 고객과 함께 공유하고자 합니다.
            </p>
          </div>

          <div className="stats-grid">
            {statItems.map((s, i) => (
              <div key={i} className="stat-item">
                <div className="stat-number">
                  <b data-value={s.value}>0</b>
                  <span className="stat-unit">{s.unit || ''}</span>
                </div>
                <div className="stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ Reviews: HomePage와 동일한 구조/클래스 (가로 스크롤) */}
      <section className="reviews">
        <div className="container">
          <div className="reviews-header">
            <h2 className="reviews-title">다이빙이지와 함께한 리뷰</h2>
            <p className="reviews-description">다이빙이지와 함께한 생생한 리뷰를 만나보세요.</p>
          </div>

          <div className="reviews-scroll">
            {reviews.map((r, i) => (
              <div className="review-card" key={i}>
                <div className="review-image">
                  <img src={r.avatar} alt={`${r.name} 후기`} />
                </div>
                <p className="review-text">"{r.text}"</p>
                <p className="review-name">강습생 {r.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 방송/액티비티 */}
      <section className="activities">
        <div className="container">
          <div className="activities-header">
            <h2>다이빙이지×니돈내산 독박투어</h2>
            <p>채널S의 여행 예능 '니돈내산 독박투어'코타키나발루 편에서 소중한 경험을 함께 했습니다.</p>
          </div>
          <div className="activities-grid">
            {activities.map((a, idx) => (
              <div className="activity-card" key={idx}>
                <img src={a.img} alt={a.title} />
                <div className="activity-content">
                  <h3>{a.title}</h3>
                  <p>현장감 있는 다이빙 경험을 제공합니다.</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
